import React, { useContext, useEffect, useState } from 'react';

import { Box, styled, Typography } from '@material-ui/core';
import { OneOnOneInstanceContext as DeerOneOnOneInstanceContext } from 'Deer/pages/MyData/OneOnOne/LegacyInstancePage/LegacyInstancePage';
import isAfter from 'date-fns/isAfter';
import { useDispatch } from 'react-redux';
import TalkingPoints from 'screens/common/sidepanel/CheckIn/TalkingPoints';
import ActionItems from 'screens/oneOnOne/actionItems';
import { StyledButton, StyledPaper } from 'styles/common';
import { checkMeshRecommendationsEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ONE_ON_ONE_STATUSES } from 'constants/ProjectConstants';
import { convertDateToString11, convertDateToString7 } from 'constants/helper';
import {
  sendClickedOnCancelEvent,
  sendClickedOnEndMeetingEvent,
  sendClickedOnReopenEvent,
} from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { showErrorSnackbar } from 'services/snackbar';
import { acknowledgeOneOnOne, saveOneOnOneRating } from 'services/task';

import { useIsLegacyOneOnOneInstance } from '../hooks/useIsLegacyOneOnOneInstance';
import { BottomPanel } from './BottomPanel';
import { CancelMeetingConfirmationModal } from './CancelMeetingConfirmationModal';
import { EndMeetingConfirmationModal } from './EndMeetingConfirmationModal';
import { FeedbackCard } from './FeedbackCard';
import Header from './Header';
import { OneOnOneInstanceContext } from './InstancePageContext';
import MeshRecommends from './MeshRecommends';

const Questions = ({ setCurrentView, reportee }) => {
  const contextToUse = checkNewNavEnabled() ? DeerOneOnOneInstanceContext : OneOnOneInstanceContext;
  const {
    state: { instanceDetails, otherEmployee, loggedInEmployee, currentTimeline, endMeetingModal, cancelMeetingModal },
    updateOneOnOne,
    toggleEndMeetingModal,
    toggleCancelMeetingModal,
    dateTimeChangeHandler,
    updateDetailedTimeline,
  } = useContext(contextToUse);

  const { coloredStartDate, coloredDueDate } = instanceDetails;

  // THE USER CAN START A FUTURE MEETING BUT CAN ONLY MARK IT COMPLETE AFTER THE START DATE/TIME
  const canMarkAsComplete = !isAfter(new Date(coloredStartDate.timeStamp), new Date(Date.now()));

  const dispatch = useDispatch();

  const { isLegacyOneOnOneInstance } = useIsLegacyOneOnOneInstance();

  const [enableMarkAsComplete, setEnableMarkAsComplete] = useState(false);
  const [clickedMarkAsComplete, setClickedMarkAsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [lastUpdatedTime, setlastUpdatedTime] = useState('-');
  const [starRating, setstarRating] = useState(0);

  const isManager = instanceDetails.from.id === loggedInEmployee.id;

  const filterEmpIds = Array.isArray(instanceDetails?.toIds) ? instanceDetails.toIds.map((empItem) => empItem.id) : [];

  const meetingStatus = instanceDetails.checkInState;

  const isSubmitted = [ONE_ON_ONE_STATUSES.ACKNOWLEDGED, ONE_ON_ONE_STATUSES.CANCELLED].includes(
    instanceDetails.checkInState
  );

  useEffect(() => {
    if (currentTimeline?.lastActivityTS) {
      // WILL SET TIME ONLY IF OBJECT IS NOT EMPTY
      const modifiedTime = `${convertDateToString11(
        new Date(currentTimeline?.lastActivityTS)
      )} at  ${convertDateToString7(new Date(currentTimeline?.lastActivityTS), true)}`;
      setlastUpdatedTime(modifiedTime);

      if (currentTimeline?.stats?.rating) {
        const modifiedRating = currentTimeline?.stats?.rating;
        setstarRating(modifiedRating);
      }
    }
  }, [currentTimeline?.lastActivityTS, currentTimeline?.stats?.rating]);

  const editorName = currentTimeline?.editorName;

  const handleMarkAsComplete = () => {
    setIsLoading(true);
    if (enableMarkAsComplete) {
      acknowledgeOneOnOne({
        checkInId: instanceDetails?.id,
        status: ONE_ON_ONE_STATUSES.ACKNOWLEDGED,
      })
        .then((res) => {
          setIsLoading(false);
          // THIS WILL UPDATE THE LATEST RESPONSE IN CONTEXT STATE
          updateOneOnOne({ checkInState: res?.[0] });
          updateDetailedTimeline(instanceDetails.id, res?.[0]);
          sendClickedOnEndMeetingEvent({
            eventProperties: {
              role: loggedInEmployee.userRole,
            },
          });
        })
        .catch((err) => dispatch(showErrorSnackbar(err?.message || 'Status updation failed.')));
    } else {
      setClickedMarkAsComplete(true);
      toggleEndMeetingModal();
    }
  };

  const endMeetingWithoutMandatoryQuestions = () => {
    setIsLoading(true);
    acknowledgeOneOnOne({
      checkInId: instanceDetails?.id,
      status: ONE_ON_ONE_STATUSES.ACKNOWLEDGED,
    })
      .then((res) => {
        setIsLoading(false);
        // THIS WILL UPDATE THE LATEST RESPONSE IN CONTEXT STATE
        updateOneOnOne({ checkInState: res?.[0] });
        updateDetailedTimeline(instanceDetails.id, res?.[0]);
        sendClickedOnEndMeetingEvent({
          eventProperties: {
            role: loggedInEmployee.userRole,
          },
        });
        toggleEndMeetingModal();
        setClickedMarkAsComplete(false);
      })
      .catch((err) => dispatch(showErrorSnackbar(err?.message || 'Status updation failed.')));
  };

  const handleCancelAction = () => {
    acknowledgeOneOnOne({
      checkInId: instanceDetails?.id,
      status: ONE_ON_ONE_STATUSES.CANCELLED,
    })
      .then((res) => {
        // THIS WILL UPDATE THE LATEST RESPONSE IN CONTEXT STATE
        updateOneOnOne({ checkInState: res?.[0] });
        updateDetailedTimeline(instanceDetails.id, res?.[0]);
        sendClickedOnCancelEvent({
          eventProperties: {
            role: loggedInEmployee.userRole,
          },
        });
      })
      .catch((err) => dispatch(showErrorSnackbar(err?.message || 'Status updation failed.')));
  };

  const handleReOpenAction = () => {
    acknowledgeOneOnOne({
      checkInId: instanceDetails?.id,
      status: ONE_ON_ONE_STATUSES.ACTIVE,
    })
      .then((res) => {
        // THIS WILL UPDATE THE LATEST RESPONSE IN CONTEXT STATE
        updateOneOnOne({ checkInState: res?.[0] });
        updateDetailedTimeline(instanceDetails.id, res?.[0]);
        sendClickedOnReopenEvent({
          eventProperties: {
            role: loggedInEmployee.userRole,
          },
        });
      })
      .catch((err) => dispatch(showErrorSnackbar(err?.message || 'Status updation failed.')));
  };

  const saveRating = (rating) => {
    saveOneOnOneRating({ rating, checkInId: instanceDetails.id })
      .then(() => {
        setstarRating(rating);
      })
      .catch(() => {
        dispatch(showErrorSnackbar('Sorry an error occurred while saving the rating'));
      });
  };

  return (
    <>
      <Header
        instanceDetails={instanceDetails}
        otherEmployee={otherEmployee}
        isManager={isManager}
        isSubmitted={isSubmitted}
        handleMarkAsComplete={handleMarkAsComplete}
        handleReOpen={handleReOpenAction}
        isLoading={isLoading}
        isDisabled={isLegacyOneOnOneInstance}
      />
      {isSubmitted && (
        <Box marginBottom="2rem" paddingLeft="1rem">
          <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
            {`This meeting was ${meetingStatus === ONE_ON_ONE_STATUSES.ACKNOWLEDGED ? 'completed' : 'cancelled'}`}
            {editorName && lastUpdatedTime ? ` by ${editorName} on ${lastUpdatedTime}.` : `.`}
          </Typography>
        </Box>
      )}

      {checkMeshRecommendationsEnabled() && !isLegacyOneOnOneInstance ? (
        <MeshRecommends isManager={isManager} reportee={reportee} oneOnOneInstanceId={instanceDetails.id} />
      ) : null}

      <TalkingPoints
        oneOnOneInstanceId={instanceDetails.id}
        isSubmitted={isSubmitted}
        isManager={isManager}
        clickedMarkAsComplete={clickedMarkAsComplete}
        setEnableMarkAsComplete={setEnableMarkAsComplete}
        taskDetails={instanceDetails}
        isDisabled={isLegacyOneOnOneInstance}
      />
      <StyledPaper elevation={0} showBorder padding="1rem" bottomGutter={2}>
        <ActionItems
          checkInInstanceId={instanceDetails.id}
          checkInParentId={instanceDetails?.parentTaskId}
          filterEmpIds={filterEmpIds}
          isDisabled={isSubmitted || isLegacyOneOnOneInstance}
        />
      </StyledPaper>
      <StyledPaper elevation={0} showBorder padding="1rem" bottomGutter={2}>
        <FeedbackCard
          isDisabled={isSubmitted || isLegacyOneOnOneInstance}
          saveRating={saveRating}
          prevVal={starRating}
        />
      </StyledPaper>

      <BottomPanel
        lastUpdatedTime={lastUpdatedTime}
        editorName={editorName}
        status={meetingStatus}
        enableMarkAsComplete={enableMarkAsComplete}
        startDateTime={coloredStartDate.timeStamp ? new Date(coloredStartDate.timeStamp) : null}
        endDateTime={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
        onDateTimeChange={dateTimeChangeHandler}
        toggleCancelMeetingModal={toggleCancelMeetingModal}
        handleMarkAsComplete={handleMarkAsComplete}
        canMarkAsComplete={canMarkAsComplete && !isLegacyOneOnOneInstance}
        isLoading={isLoading}
      />

      <EndMeetingConfirmationModal
        open={endMeetingModal}
        onConfirm={endMeetingWithoutMandatoryQuestions}
        onCancel={toggleEndMeetingModal}
        isLoading={isLoading}
      />
      <CancelMeetingConfirmationModal
        open={cancelMeetingModal}
        onConfirm={handleCancelAction}
        onCancel={toggleCancelMeetingModal}
      />
    </>
  );
};

export default Questions;

export const CustomStyledButton = styled(StyledButton)`
  line-height: 1;
  padding: 13px;
  width: 160px;

  @media (max-width: 1439px) {
    width: 135px;
  }
`;
